require("./threepl-create-train-voucher-view.scss");

import {ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {BzDialogService, BzSelectOption} from "@bringoz/ui";
import {bzIconArrowLeft, bzIconPlus} from "@bringoz/ui/icons";
import * as _ from "lodash";
import {TrainContainerDetails} from "../../../models/train-container-details.model";
import {ThreePlExtraData} from "../../../models/threepl-extra-data.model";
import {ThreePLProviderType} from "../../../types/threePL-provider.type";
import {
  HazardousMaterial, TrainCar, TrainProviderExternalData, TrainStation, TrainStationExtension, UnNumberOption,
} from "../../models/train-provider-settings.model";
import {ThreePLTrainService} from "../../services/threepl-train.service";
import {TrainContainersValidity} from "../../state/train-containers-validity";
import {
  ContainerChange, SharedCarChange,
} from "../threepl-train-voucher-containers-table/threepl-train-voucher-containers-table.component";
import {SupplyAccountChange} from "../threepl-supply-account-select/threepl-supply-account-select.component";
import {trainCarKey} from "../../models/train-car-key";
import {FulfillmentDialogService} from "../../../../fulfillment/services/fulfillment-dialogs-service.service";
import * as moment from "moment";

export interface SaveVoucherEvent {
  threePlExtraData: ThreePlExtraData;
  supplyId: string | undefined;
  date: number;
}

function parseJsonValues<T>(json: string | undefined): T[] {
  return json ? _.values(JSON.parse(json)) as T[] : [];
}

@Component({
  selector: "app-threepl-create-train-voucher-view",
  templateUrl: "./threepl-create-train-voucher-view.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TrainContainersValidity],
})
export class ThreeplCreateTrainVoucherViewComponent implements OnInit {
  readonly threePlExtraData = input<ThreePlExtraData>();
  readonly containers = input<TrainContainerDetails[]>();

  readonly back = output<void>();
  readonly saveVoucher = output<SaveVoucherEvent>();

  readonly icons = {back: bzIconArrowLeft, plus: bzIconPlus};
  readonly supplyAccountType = "ThreePLSupply";

  readonly extraData = signal<ThreePlExtraData>(undefined);
  readonly containerList = signal<TrainContainerDetails[]>([]);
  readonly date = signal<number>(undefined);
  readonly supplyId = signal<string | undefined>(undefined);
  readonly submitted = signal(false);
  readonly totalCars = signal<number | undefined>(undefined);
  readonly sharedCarsMap = signal<Record<string, boolean>>({});

  readonly trainCars = signal<TrainCar[]>([]);
  readonly trainCarTypes = signal<TrainCar[]>([]);
  readonly hazardousMaterials = signal<HazardousMaterial[]>([]);
  readonly unNumbers = signal<UnNumberOption[]>([]);
  readonly trainStations = signal<TrainStation[]>([]);
  readonly trainStationExtensions = signal<TrainStationExtension[]>([]);

  readonly pickupStationId = signal<string | null>(null);
  readonly destinationStationId = signal<string | null>(null);
  readonly pickupTrainStationExtensions = signal<TrainStationExtension[]>([]);
  readonly destinationTrainStationExtensions = signal<TrainStationExtension[]>([]);
  readonly pickupTrainStationExtension = signal<TrainStationExtension | undefined>(undefined);
  readonly destinationTrainStationExtension = signal<TrainStationExtension | undefined>(undefined);

  readonly dateHolder = computed(() => ({date: this.date()}));

  readonly stationOptions = computed<BzSelectOption<string>[]>(() =>
    this.trainStations().map((s) => ({value: s.id, label: `${s.id} ${s.description}`})));
  readonly pickupExtensionOptions = computed(() => this.extensionOptions(this.pickupTrainStationExtensions()));
  readonly destinationExtensionOptions = computed(() => this.extensionOptions(this.destinationTrainStationExtensions()));

  readonly formValid = computed(() =>
    !!this.supplyId() && !!this.pickupStationId() && !!this.destinationStationId() && this.validity.valid());

  private readonly validity = inject(TrainContainersValidity);
  private readonly trainService = inject(ThreePLTrainService);
  private readonly fulfillmentDialogService = inject<FulfillmentDialogService>("fulfillmentDialogService" as never);
  private readonly dialog = inject(BzDialogService);
  private readonly translate = inject(TranslateService);
  private readonly moment = inject<typeof moment>("moment" as never);

  ngOnInit(): void {
    this.date.set(this.moment().valueOf());
    this.containerList.set(this.containers() ?? []);
    this.extraData.set(this.threePlExtraData() ?? {
      type: ThreePLProviderType.TrainIL,
      name: undefined,
      carsDetails: [],
      approvalStatus: undefined,
      pickupDepot: undefined,
      destinationDepot: undefined,
      pickupTrainStation: undefined,
      destinationTrainStation: undefined,
    });
    this.totalCars.set(this.extraData().totalCars);
    this.loadTrainSettings();
  }

  onNameChange(name: string): void {
    this.extraData().name = name;
  }

  onChangeSupplyId({id}: SupplyAccountChange): void {
    this.supplyId.set(id);
  }

  onAddContainer(): void {
    const from = this.moment(this.date()).startOf("day").valueOf();
    const to = this.moment(this.date()).endOf("day").valueOf();
    const excludeIds = _.uniq(_.map(this.containerList(), "stepId"));
    const extra = this.extraData();
    Promise.resolve(this.fulfillmentDialogService.selectSteps(from, to, excludeIds, [extra.pickupDepotId], [extra.destinationDepotId]))
      .then((steps) => {
        const toAdd = this.trainService.getContainersBySteps(steps);
        this.containerList.update((containers) => _.concat(containers, toAdd));
      })
      .catch(() => undefined);
  }

  onChangeContainer({container, index}: ContainerChange): void {
    const copy = _.cloneDeep(this.containerList());
    copy.splice(index, 1, container);
    this.containerList.set(copy);
    this.setTotalCars();
  }

  onIsSharedCarChange({carId, isSharedCar}: SharedCarChange): void {
    const containersOnSameCar = this.containerList().filter((c) => c.carId === carId);
    if (containersOnSameCar.length > 1) {
      this.dialog.confirm({
        title: `Shared ${carId}`,
        message: this.translate.instant("THREEPL_TRAIN_SHARED_CAR_INFO"),
        confirmLabel: this.translate.instant("Confirm"),
        cancelLabel: this.translate.instant("Cancel"),
      }).then((confirmed) => {
        if (confirmed) {
          this.setShared(carId, isSharedCar);
        }
      });
    } else {
      this.setShared(carId, isSharedCar);
    }
  }

  onRemoveContainer({index}: ContainerChange): void {
    const copy = _.cloneDeep(this.containerList());
    copy.splice(index, 1);
    this.containerList.set(copy);
    this.setTotalCars();
  }

  onPickupTrainStation(stationId: string): void {
    const station = this.trainStations().find((s) => s.id === stationId);
    this.pickupStationId.set(stationId);
    this.pickupTrainStationExtensions.set(this.trainStationExtensions().filter((e) => e.stationId === station.id));
    this.extraData().pickupTrainStation = {stationId: station.id, stationDescription: station.description};
  }

  onDestinationTrainStation(stationId: string): void {
    const station = this.trainStations().find((s) => s.id === stationId);
    this.destinationStationId.set(stationId);
    this.destinationTrainStationExtensions.set(this.trainStationExtensions().filter((e) => e.stationId === station.id));
    this.extraData().destinationTrainStation = {stationId: station.id, stationDescription: station.description};
  }

  onPickupTrainStationExtension(extensionId: string | null): void {
    const extension = this.pickupTrainStationExtensions().find((e) => e.extensionId === extensionId);
    this.pickupTrainStationExtension.set(extension);
    this.extraData().pickupTrainStation.extensionId = extension?.extensionId;
    this.extraData().pickupTrainStation.extensionDescription = extension?.description;
  }

  onDestinationTrainStationExtension(extensionId: string | null): void {
    const extension = this.destinationTrainStationExtensions().find((e) => e.extensionId === extensionId);
    this.destinationTrainStationExtension.set(extension);
    this.extraData().destinationTrainStation.extensionId = extension?.extensionId;
    this.extraData().destinationTrainStation.extensionDescription = extension?.description;
  }

  onSave(): void {
    this.submitted.set(true);
    if (!this.formValid()) {
      return;
    }
    const extra = this.extraData();
    const containersByCar = _.groupBy(this.containerList(), "carId");
    _.forOwn(containersByCar, (containers, carId) => {
      const carsDetailsIndex = _.findIndex(extra.carsDetails, (car) => car.id === carId);
      if (carsDetailsIndex > -1) {
        extra.carsDetails[carsDetailsIndex].trainContainerDetails = _.concat(extra.carsDetails[carsDetailsIndex].trainContainerDetails, containers);
      } else {
        const carSettings = _.find(this.trainCars(), (car) => car.carId === carId);
        extra.carsDetails.push({
          id: carSettings.id,
          carType: carSettings.carType,
          allowedWeightInTon: carSettings.capacity ? _.toNumber(carSettings.capacity) : undefined,
          trainContainerDetails: containers,
          shared: this.sharedCarsMap()[carSettings.carId],
        });
      }
    });
    this.saveVoucher.emit({threePlExtraData: extra, supplyId: this.supplyId(), date: this.date()});
  }

  private extensionOptions(extensions: TrainStationExtension[]): BzSelectOption<string | null>[] {
    return [
      {value: null, label: this.translate.instant("None")},
      ...extensions.map((e) => ({value: e.extensionId, label: `${e.id} ${e.description}`})),
    ];
  }

  private setShared(carId: string, isSharedCar: boolean): void {
    this.sharedCarsMap.update((map) => ({...map, [carId]: isSharedCar}));
  }

  private setTotalCars(): void {
    const totalCars = _.uniq(this.containerList().map((c) => c.carId).filter((carId) => !!carId)).length;
    this.extraData().totalCars = totalCars;
    this.totalCars.set(totalCars);
  }

  private loadTrainSettings(): void {
    this.trainService.getTrainProviderExternalData().then((settings: TrainProviderExternalData) => {
      this.trainCars.set(parseJsonValues<TrainCar>(settings.trainCarTypeJson).map((car) => ({...car, carId: trainCarKey(car, car.capacity)})));
      this.hazardousMaterials.set(parseJsonValues<HazardousMaterial>(settings.hazardousMaterialsJson));
      this.trainCarTypes.set(parseJsonValues<TrainCar>(settings.trainCarTypeJson));
      this.unNumbers.set(parseJsonValues<UnNumberOption>(settings.unNumberJson));
      this.trainStations.set(parseJsonValues<TrainStation>(settings.trainStationsJson));
      this.trainStationExtensions.set(parseJsonValues<TrainStationExtension>(settings.trainStationExtensionsJson));
    }).catch(() => undefined);
  }
}
